import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Check, X } from 'lucide-react';
import { api } from '../../api/client';
import type { OwnerSummary } from '@pay/shared';

export default function Owners() {
  const [owners, setOwners] = useState<OwnerSummary[]>([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: '',
    orgnr: '',
    contact_email: '',
    sender_name: '',
    sender_email: '',
  });

  function refresh() {
    api.get<OwnerSummary[]>('/api/admin/owners').then(setOwners).catch(() => {});
  }
  useEffect(refresh, []);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    await api.post('/api/admin/owners', {
      ...form,
      sender_name: form.sender_name || form.name,
      sender_email: form.sender_email || form.contact_email,
    });
    setCreating(false);
    setForm({ name: '', orgnr: '', contact_email: '', sender_name: '', sender_email: '' });
    refresh();
  }

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Eiere</h1>
        <button
          onClick={() => setCreating((v) => !v)}
          className="flex items-center gap-2 bg-brand hover:bg-brand-dark text-white rounded px-3 py-2 text-sm font-medium"
        >
          <Plus size={16} /> Ny eier
        </button>
      </div>

      {creating && (
        <form onSubmit={create} className="bg-white rounded-lg shadow border border-slate-200 p-5 mb-5 grid grid-cols-2 gap-4">
          <Field label="Navn">
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="input" required />
          </Field>
          <Field label="Org.nr">
            <input value={form.orgnr} onChange={(e) => setForm((f) => ({ ...f, orgnr: e.target.value }))} className="input" required />
          </Field>
          <Field label="Kontakt-e-post">
            <input type="email" value={form.contact_email} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))} className="input" required />
          </Field>
          <Field label="Avsendernavn (e-post)">
            <input value={form.sender_name} onChange={(e) => setForm((f) => ({ ...f, sender_name: e.target.value }))} className="input" placeholder="(samme som navn)" />
          </Field>
          <Field label="Avsender-e-post (Reply-To)">
            <input type="email" value={form.sender_email} onChange={(e) => setForm((f) => ({ ...f, sender_email: e.target.value }))} className="input" placeholder="(samme som kontakt)" />
          </Field>
          <div className="col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setCreating(false)} className="px-3 py-2 text-sm">Avbryt</button>
            <button type="submit" className="bg-brand hover:bg-brand-dark text-white rounded px-3 py-2 text-sm font-medium">
              Opprett
            </button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg shadow border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left px-6 py-3">Navn</th>
              <th className="text-left px-6 py-3">Org.nr</th>
              <th className="text-left px-6 py-3">Vipps</th>
              <th className="text-left px-6 py-3">Aktiv</th>
            </tr>
          </thead>
          <tbody>
            {owners.map((o) => (
              <tr key={o.id} className="border-t border-slate-100">
                <td className="px-6 py-3">
                  <Link to={`/admin/owners/${o.id}`} className="text-brand hover:underline font-medium">
                    {o.name}
                  </Link>
                </td>
                <td className="px-6 py-3 text-slate-600">{o.orgnr}</td>
                <td className="px-6 py-3">
                  {o.has_active_vipps ? <Check size={16} className="text-green-600" /> : <X size={16} className="text-slate-400" />}
                </td>
                <td className="px-6 py-3">
                  {o.active ? <Check size={16} className="text-green-600" /> : <X size={16} className="text-slate-400" />}
                </td>
              </tr>
            ))}
            {owners.length === 0 && (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                  Ingen eiere ennå. Klikk "Ny eier" for å starte.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <style>{`.input { width:100%; border:1px solid #cbd5e1; border-radius:4px; padding:6px 10px; }`}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1">{label}</span>
      {children}
    </label>
  );
}
